import { CurrencyPipe, DOCUMENT, isPlatformBrowser, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    PLATFORM_ID,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import {
    AnchorDirective,
    isHCM,
    Platform,
    PLATFORM_TOKEN,
    RouterLinkDirective,
    TrackingDirective,
    VehicleFeature,
    ViewportDetectionService,
} from '@hyundai/ng-common-lib';
import {
    M000C010ImageComponent,
    M000C040LoadingSpinnerComponent,
    M010C020IconLinkComponent,
    M030C160VehicleGalleryComponent,
} from '@hyundai/ui';
import { Store } from '@ngrx/store';
import { DialogService } from '@pixelgenau/ngx-dialog';
import { switchMap } from 'rxjs';
import { BatteryOverlayComponentDialogTag } from '../../overlays/battery-overlay/battery-overlay.dialog';
import { ImprintOverlayComponentDialogTag } from '../../overlays/imprint-overlay/imprint-overlay.dialog';
import { PromiseOverlayComponentDialogTag } from '../../overlays/promise-overlay/promise-overlay.dialog';
import { SimilarVehiclesComponent } from '../../sections/similar-vehicles/similar-vehicles.component';
import { VehicleDetailInfosComponent } from '../../sections/vehicle-detail-infos/vehicle-detail-infos.component';
import { VehicleEquipmentComponent } from '../../sections/vehicle-equipment/vehicle-equipment.component';
import { setFilterQuery } from '../../utils/filter-query-setter';

@Component({
    standalone: true,
    templateUrl: './detail-page.component.html',
    styleUrl: './detail-page.component.scss',
    imports: [
        M010C020IconLinkComponent,
        CurrencyPipe,
        VehicleDetailInfosComponent,
        VehicleEquipmentComponent,
        SimilarVehiclesComponent,
        M000C010ImageComponent,
        M030C160VehicleGalleryComponent,
        RouterLinkDirective,
        AnchorDirective,
        M000C040LoadingSpinnerComponent,
        TrackingDirective,
        NgClass,
    ],
    selector: 'lib-detail-page',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export default class DetailPageComponent {
    PlatformType = Platform;

    renderer = inject(Renderer2);
    document = inject(DOCUMENT);
    route = inject(ActivatedRoute);
    platform = inject(PLATFORM_TOKEN);
    store = inject(Store);
    dialogService = inject(DialogService);
    platformId = inject(PLATFORM_ID);
    cdr = inject(ChangeDetectorRef);
    destroyRef = inject(DestroyRef);
    viewport = inject(ViewportDetectionService);

    isHCM = isHCM();

    vehicle = this.store.selectSignal(VehicleFeature.vehicleState.selectLoadedDetail);
    isLoading = this.store.selectSignal(VehicleFeature.vehicleState.selectIsLoadingDetail);
    similarVehicles = this.store.selectSignal(VehicleFeature.vehicleState.selectSimilarList);
    loadedSimilarVehicles = this.store.selectSignal(VehicleFeature.vehicleState.selectLoadedSimilarList);

    dealerIds = computed(() => {
        const dealer = this.vehicle().dealer;
        return dealer ? [dealer.id, ...(dealer.subDealer ?? [])] : [];
    });

    dealerParams = computed(() => {
        return setFilterQuery({ dealerId: this.dealerIds().join(',') });
    });

    similarVehiclesFilter = computed(() => {
        const loadedSimilarVehicles = this.loadedSimilarVehicles();
        const query = loadedSimilarVehicles?.query || '';
        if (!query) return;

        const { firstRegistrationYear, model, mileage, price } = query;
        return {
            filter: JSON.stringify({
                firstRegistrationYear,
                model,
                mileage,
                price,
            }),
        };
    });

    linkToVehicleList = computed(() => {
        if (this.isHCM) return '/fahrzeugbestand';
        return `/${this.vehicle().dealer.parentDealerSlug ?? this.vehicle().dealer.slug}/fahrzeugbestand`;
    });

    constructor() {
        if (isPlatformBrowser(inject(PLATFORM_ID))) {
            sessionStorage.removeItem('cart');
        }

        this.destroyRef.onDestroy(() => {
            this.richDataScriptElement?.remove();
        });
    }

    async openPromiseOverlay(): Promise<void> {
        this.dialogService
            .openDialog(PromiseOverlayComponentDialogTag)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((result) => result.close$),
            )
            .subscribe(() => {
                // MW vorrübergehend genehmigt
                // eslint-disable-next-line @rx-angular/no-explicit-change-detection-apis
                this.cdr.detectChanges();
            });
    }

    async openBatteryOverlay(): Promise<void> {
        this.dialogService
            .openDialog(BatteryOverlayComponentDialogTag)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((result) => result.close$),
            )
            .subscribe(() => {
                // MW vorrübergehend genehmigt
                // eslint-disable-next-line @rx-angular/no-explicit-change-detection-apis
                this.cdr.detectChanges();
            });
    }

    async openImprintOverlay(): Promise<void> {
        this.dialogService
            .openDialog(ImprintOverlayComponentDialogTag)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((result) => result.close$),
            )
            .subscribe(() => {
                // MW vorrübergehend genehmigt
                // eslint-disable-next-line @rx-angular/no-explicit-change-detection-apis
                this.cdr.detectChanges();
            });
    }

    share(): void {
        if (isPlatformBrowser(this.platformId) && navigator.share) {
            navigator
                .share({
                    title: document.title,
                    url: window.location.href,
                })
                .then()
                .catch(console.error);
        }
    }

    richDataScriptElement?: HTMLScriptElement;
    setVehicleRichData = effect(() => {
        const script: HTMLScriptElement = this.renderer.createElement('script') satisfies HTMLScriptElement;
        script.type = 'application/ld+json';
        script.text = this.vehicle().richData;
        this.richDataScriptElement?.remove();
        this.richDataScriptElement = script;
        this.renderer.appendChild(this.document.body, script);
    });
}
